import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';

/**
 * FilterChip - A chip-style filter button with dropdown selector.
 *
 * @param {React.Component} icon - Lucide icon component
 * @param {string} label - Filter label text
 * @param {*} value - Current filter value (null if inactive)
 * @param {string} displayValue - Text to display when filter is active
 * @param {function} onClear - Called when the X button is clicked
 * @param {React.ReactNode} children - Dropdown content rendered inside the popover
 */
const FilterChip = ({ icon: Icon, label, value, displayValue, onClear, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const isActive = value != null && value !== '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <motion.button
        className="btn btn-sm d-flex align-items-center gap-1"
        style={{
          border: isActive ? '1.5px solid var(--bs-primary)' : '1px solid #e0e0e0',
          backgroundColor: isActive ? 'rgba(59, 130, 246, 0.08)' : '#fff',
          borderRadius: '20px',
          padding: '4px 12px',
          fontSize: '0.8rem',
          fontWeight: 500,
          color: isActive ? 'var(--bs-primary)' : 'var(--bs-body-color)',
          whiteSpace: 'nowrap',
        }}
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {Icon && <Icon size={14} />}
        <span>{isActive ? displayValue : label}</span>
        {isActive ? (
          <X
            size={14}
            style={{ cursor: 'pointer', marginLeft: '2px' }}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              setOpen(false);
            }}
          />
        ) : (
          <ChevronDown size={14} />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="bg-white border rounded shadow-sm"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              zIndex: 1050,
              minWidth: '200px',
              padding: '8px',
            }}
          >
            {typeof children === 'function' ? children(() => setOpen(false)) : children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterChip;
